import { Typography } from '@mui/material'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import Popper from '@mui/material/Popper'
import { styled } from '@mui/material/styles'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { RenderInputWithLoader } from '@components'
import { OptionCard } from '@components/formFields/option-card'
import { useAppSelector } from '@redux/hooks'
import { CustomEvent, Options } from '../../types'
import { RenderOption } from '../renderOption'
import { useFetchTemplateApprovers, useFetchUsersWithApprovalPermission } from './hooks'
import * as css from './styles'
import { t } from '@lingui/core/macro'

interface Props {
  allow_approval: boolean
  handleChangeCustom: ({ key, value }: CustomEvent) => void
  handleChangeApprovers: (values: object[]) => void
}

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
})

/**
 * Component controlling the inputs for the *Settings* section of a template.
 * @param {Props} props **React props**
 * @param {boolean} allow_approval - Checks if approval is enabled on the account
 * @param {({ key, value }: CustomEvent) => void} handleChangeCustom - The onChange handler for checkboxes
 * @param {(values: object[]) => void} handleChangeApprovers - The onChange handler for updating approvers
 */
export const SettingsSection = ({
  allow_approval,
  handleChangeCustom,
  handleChangeApprovers,
}: Props) => {
  const [ENABLE_AUTO_SUBMIT = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_AUTO_SUBMIT]),
  )
  const { approvers, settings } = useAppSelector(state => state.Template.template)
  const features = useAppSelector(state => state.Auth.features)
  const { usersWithApprovalPermission, loadingUsers } = useFetchUsersWithApprovalPermission(
    settings?.require_approval,
  )
  const loadingSavedApprovers = useFetchTemplateApprovers(
    handleChangeApprovers,
    settings?.require_approval,
  )
  const loading = loadingSavedApprovers || loadingUsers
  const showRequireApproval = Boolean(features?.approval) && Boolean(allow_approval)

  const getOptionSelected = (option, value) => option.id === value.id

  return (
    <>
      <Typography variant="h3" gutterBottom css={css.sectionTitle}>
        {t`Settings`}
      </Typography>
      <div
        className="grid flex-wrap gap-3 mb-3"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 340px))' }}
      >
        <OptionCard
          id="require-identifier"
          onChangeCallback={isChecked => {
            handleChangeCustom({ key: Options.REQUIRE_IDENTIFIER, value: isChecked ? 1 : 0 })
          }}
          defaultChecked={Boolean(settings?.require_identifier)}
        >
          <OptionCard.Title id="require-identifier">{t`Require submission name`}</OptionCard.Title>
          <OptionCard.Summary>
            {t`Submission names are a great way for users to provide context about the data being manually submitted.`}
          </OptionCard.Summary>
        </OptionCard>

        {showRequireApproval ? (
          <OptionCard
            id="require-approval"
            onChangeCallback={isChecked => {
              handleChangeCustom({ key: Options.REQUIRE_APPROVAL, value: isChecked ? 1 : 0 })
            }}
            defaultChecked={Boolean(settings?.require_approval)}
          >
            <OptionCard.Title id="require-approval">
              {t`Require approval on changes to data`}
            </OptionCard.Title>
            <OptionCard.Summary>
              {t`Get notified when a user creates a submission; A workflow to comment, reject, or approve.`}
              <div>
                <a
                  href="https://support.claravine.com/hc/en-us/articles/4415420082445"
                  className="text-sm border-b border-cv-alice-blue-500 text-cv-alice-blue-500"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t`Learn more`}
                </a>
              </div>
            </OptionCard.Summary>

            {Boolean(settings?.require_approval) && (
              <>
                <Autocomplete
                  id="users"
                  css={css.autocomplete}
                  loading={loading}
                  loadingText={t`Loading approvers...`}
                  multiple
                  disableCloseOnSelect
                  options={usersWithApprovalPermission}
                  getOptionLabel={option => option.name}
                  isOptionEqualToValue={getOptionSelected}
                  onChange={(_, newValues) => {
                    handleChangeApprovers(newValues)
                  }}
                  renderInput={params => (
                    <RenderInputWithLoader
                      params={params}
                      loading={loading}
                      label={t`Notify users`}
                    />
                  )}
                  PopperComponent={StyledPopper}
                  renderOption={(props, options, state) => {
                    const { key, ...optionProps } = props
                    return (
                      <RenderOption
                        optionKey={key}
                        optionProps={optionProps}
                        name={options.name}
                        state={state}
                      />
                    )
                  }}
                  value={approvers || []}
                />
                <Typography variant="caption">
                  {t`Enter a list of administrators to receive email notifications when data is added or edited on this template.`}
                </Typography>
              </>
            )}
          </OptionCard>
        ) : null}

        {ENABLE_AUTO_SUBMIT ? (
          <OptionCard
            id="autosubmit-inbound"
            onChangeCallback={isChecked => {
              handleChangeCustom({ key: Options.ENABLE_AUTO_SUBMIT, value: isChecked ? 1 : 0 })
            }}
            defaultChecked={Boolean(settings?.enable_auto_submit)}
          >
            <OptionCard.Title id="autosubmit-inbound">
              {t`Automatically submit inbound data`}
            </OptionCard.Title>
            <OptionCard.Summary>
              {t`Data without errors will automatically be added to the dataset and exported to outbound connectors.`}
            </OptionCard.Summary>
          </OptionCard>
        ) : null}
      </div>
    </>
  )
}
